import type { NpcEncounter, NpcHullTier, NpcUnit, StarSystem } from "./types";
import { systemsWithinHops, type SystemDistance } from "./galaxyGraph";
import { generateCombatMissionBoard, type CombatMission } from "./combatMissions";
import type { DifficultyBand, PlayerCombatStats } from "./missionDifficulty";
import {
  BOARD_SLOTS,
  generateMissionsForResource,
  type MiningHullTier,
  type MiningMission
} from "./miningMissions";
import { generateHaulingMission, type HaulingMission } from "./haulingMissions";
import { generateVipTransportMission, type VipTransportMission } from "./vipTransportMissions";

// Mission Board assembly (design doc 16.33): a mission board is a live
// function of (current system, player's ship), regenerated on every visit.
// This module is the system-targeting/multi-candidate layer that sits on
// top of the already-locked per-mission formulas (combatMissions.ts,
// miningMissions.ts, haulingMissions.ts, vipTransportMissions.ts) -- none
// of those generators change; this just decides WHICH system(s) feed them.

const NEARBY_RADIUS_HOPS = 3;

function nearbySystems(currentSystem: StarSystem, systems: StarSystem[]): SystemDistance[] {
  return systemsWithinHops(currentSystem.id, NEARBY_RADIUS_HOPS, systems).filter((d) => d.hops >= 1);
}

// 16.33: harder difficulty bands reach for a higher-Danger-Tier nearby
// system; Easy/Challenge stay at or below the current system's own tier.
// Falls back to the current system whenever nothing suitable exists within
// radius. `slotIndex` cycles through all qualifying Easy/Challenge matches
// (not just the nearest) so a board's many ordinary-difficulty missions
// spread across real nearby systems instead of all piling onto the current
// one -- Very Hard/Almost Impossible stay deterministic (nearest reach /
// highest tier) since there are only 1-3 such slots per board and a
// specific "the one dangerous system nearby" read matters more there than
// variety. The exact sampling algorithm is explicitly left as "not yet
// decided" in 16.33 -- this is one reasonable, deterministic
// implementation choice, not a re-derivation of a locked formula.
export function pickSystemForBand(
  band: DifficultyBand,
  currentSystem: StarSystem,
  nearby: SystemDistance[],
  slotIndex: number
): StarSystem {
  const candidates: SystemDistance[] = [{ system: currentSystem, hops: 0 }, ...nearby];

  if (band === "Very Hard" || band === "Almost Impossible") {
    const higher = candidates.filter((c) => c.system.dangerTier > currentSystem.dangerTier);
    if (higher.length === 0) {
      return currentSystem;
    }
    if (band === "Very Hard") {
      return higher.reduce((best, c) => (c.hops < best.hops ? c : best)).system;
    }
    return higher.reduce((best, c) => {
      if (c.system.dangerTier > best.system.dangerTier) return c;
      if (c.system.dangerTier === best.system.dangerTier && c.hops < best.hops) return c;
      return best;
    }).system;
  }

  const localMatches = candidates.filter((c) => c.system.dangerTier <= currentSystem.dangerTier);
  return localMatches[slotIndex % localMatches.length].system;
}

export interface AssignedCombatMission {
  mission: CombatMission;
  systemId: string;
}

// Combat: current system or a nearby system within 1-3 hops (16.33). The
// already-locked 11-mission curation (16.29) is unaffected -- this adds a
// system assignment on top, per-band-slot-index for Easy/Challenge variety.
export function assembleCombatBoard(
  player: PlayerCombatStats,
  hullTier: NpcHullTier,
  currentSystem: StarSystem,
  systems: StarSystem[],
  encounters: NpcEncounter[],
  units: NpcUnit[]
): AssignedCombatMission[] {
  const nearby = nearbySystems(currentSystem, systems);
  const board = generateCombatMissionBoard(player, hullTier, encounters, units);

  const bandCounts: Partial<Record<DifficultyBand, number>> = {};
  return board.map((mission) => {
    const band = mission.difficulty.band;
    const slotIndex = bandCounts[band] ?? 0;
    bandCounts[band] = slotIndex + 1;
    const system = pickSystemForBand(band, currentSystem, nearby, slotIndex);
    return { mission, systemId: system.id };
  });
}

export interface AssignedMiningMission {
  mission: MiningMission;
  systemId: string;
}

// Mining: same 1-3 hop sampling as Combat (16.33), but each assigned
// system's OWN resource list feeds that slot's mission (not the current
// system's) -- a higher-tier nearby system for a Very Hard/Almost
// Impossible slot naturally also stocks higher-tier resources (16.22),
// so reaching for danger and reaching for value line up automatically.
// Resources round-robin per assigned system so repeat visits to the same
// system still vary.
export function assembleMiningBoard(
  player: PlayerCombatStats,
  cargoM3: number,
  hullTier: MiningHullTier,
  currentSystem: StarSystem,
  systems: StarSystem[],
  encounters: NpcEncounter[],
  units: NpcUnit[]
): AssignedMiningMission[] {
  const nearby = nearbySystems(currentSystem, systems);
  const bandCounts: Partial<Record<DifficultyBand, number>> = {};
  const resourceCounts = new Map<string, number>();

  return BOARD_SLOTS.map((slot) => {
    const slotIndex = bandCounts[slot.band] ?? 0;
    bandCounts[slot.band] = slotIndex + 1;

    const system = pickSystemForBand(slot.band, currentSystem, nearby, slotIndex);
    const resourceIndex = resourceCounts.get(system.id) ?? 0;
    resourceCounts.set(system.id, resourceIndex + 1);
    const resource = system.resources[resourceIndex % system.resources.length];

    const candidates = generateMissionsForResource(resource, cargoM3, hullTier, encounters, units, player);
    const mission = candidates.find(
      (m) => m.band === slot.band && m.threatKind === slot.threatKind
    )!;

    return { mission, systemId: system.id };
  });
}

// Picks `count` items spread evenly across a sorted array's full index
// range (including both ends), rather than just the first `count` -- used
// so Hauling/VIP candidate routes span the whole near-to-far hop range
// instead of clustering on the nearest few systems.
function pickSpread<T>(sorted: T[], count: number): T[] {
  if (sorted.length <= count) {
    return sorted;
  }
  const picks: T[] = [];
  for (let i = 0; i < count; i++) {
    const index = Math.round((i * (sorted.length - 1)) / Math.max(1, count - 1));
    picks.push(sorted[index]);
  }
  return picks;
}

export interface RouteCandidate {
  origin: StarSystem;
  destination: StarSystem;
}

// Hauling/VIP: a much wider radius than Combat/Mining, multiple candidate
// routes with real destination/hop-count variety, and remote origins
// allowed (16.33/16.26) -- restricting to a tight radius would defeat
// 16.26's whole "plan a multi-system route" feature. Shared by both
// assembleHaulingBoard and assembleVipBoard so their route variety/remote-
// origin logic isn't duplicated.
export function sampleRouteCandidates(
  currentSystem: StarSystem,
  systems: StarSystem[],
  count: number
): RouteCandidate[] {
  const reachableFromCurrent = systemsWithinHops(currentSystem.id, Infinity, systems).filter(
    (d) => d.hops > 0
  );
  const sortedByHops = [...reachableFromCurrent].sort((a, b) => a.hops - b.hops);

  const localCount = Math.max(1, count - 1);
  const localDestinations = pickSpread(sortedByHops, localCount).map((d) => d.system);
  const candidates: RouteCandidate[] = localDestinations.map((destination) => ({
    origin: currentSystem,
    destination
  }));

  // One remote-origin candidate (16.26: "a hauling job whose origin isn't
  // wherever the player is standing right now") -- origin a few hops away,
  // destination picked from that origin's own farthest reach for a genuinely
  // different route shape, not just current-system-with-an-extra-hop.
  const remoteOriginPick = sortedByHops.find((d) => d.hops >= 2 && d.hops <= 4);
  if (remoteOriginPick && candidates.length < count) {
    const remoteOrigin = remoteOriginPick.system;
    const reachableFromRemote = systemsWithinHops(remoteOrigin.id, Infinity, systems).filter(
      (d) => d.hops > 0 && d.system.id !== currentSystem.id
    );
    const farthest = [...reachableFromRemote].sort((a, b) => b.hops - a.hops)[0];
    if (farthest) {
      candidates.push({ origin: remoteOrigin, destination: farthest.system });
    }
  }

  return candidates.slice(0, count);
}

// Recommended board size 4-6 (16.33) -- default 5.
export function assembleHaulingBoard(
  currentSystem: StarSystem,
  hullTier: NpcHullTier,
  systems: StarSystem[],
  count = 5
): HaulingMission[] {
  return sampleRouteCandidates(currentSystem, systems, count).map(({ origin, destination }) =>
    generateHaulingMission(origin, destination, hullTier, systems)
  );
}

// Recommended board size 2-3 (16.33: VIP stays rarer/more selective than
// ordinary Hauling work) -- default 2. Routes where the ship's cargo can't
// fit even 1 VIP at this tier are gated out (vipTransportMissions.ts),
// same as a single-route call.
export function assembleVipBoard(
  currentSystem: StarSystem,
  hullTier: NpcHullTier,
  cargoM3: number,
  systems: StarSystem[],
  count = 2
): VipTransportMission[] {
  return sampleRouteCandidates(currentSystem, systems, count)
    .map(({ origin, destination }) =>
      generateVipTransportMission(origin, destination, hullTier, cargoM3, systems)
    )
    .filter((m): m is VipTransportMission => m !== null);
}
